import { useState, useEffect, useImperativeHandle, forwardRef, useMemo } from "react";
import { Button, Drawer } from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import { equals } from "ramda";
import PropTypes from "prop-types";
import { useInstantSearch, useSortBy } from "react-instantsearch";

import { SORT_ITEMS, ALGOLIA_FILTERS } from "enums";
import { useAlgolia, useRouter } from "hooks";
import { ALL_CONTENT_TYPES, createAllOption } from "enums/contentTypes";

import Header from "./components/Header";
import NumberOfPeople from "./components/NumberOfPeople";
import ContentTypes from "./components/ContentTypes";
import SortBy from "./components/SortBy";
import { StyledDivider, ApplyContainer, DrawerListWrapper, FilterSortButton, FilterSortBadge } from "./styles";

const FilterSort = forwardRef((props, ref) => {
  const { options } = useSortBy({ items: Object.values(SORT_ITEMS) });
  const { setIndexUiState } = useInstantSearch();
  const { hidden = false, onClear = () => {}, contentTypeOptions = ALL_CONTENT_TYPES, collectionsFilter } = props;
  const contentTypes = useMemo(() => {
    return [createAllOption(contentTypeOptions), ...contentTypeOptions];
  }, [contentTypeOptions]);
  const { location } = useRouter();
  const {
    setSortFilterSelected,
    setNumberOfPeopleFilters,
    setContentTypesFilters,
    contentTypesFilters,
    numberOfPeopleFilters,
    sortFilterSelected,
  } = useAlgolia();
  const numberOfPeople = [5, 4, 3, 2, 1];
  const defaultFilters = useMemo(
    () => ({
      numberOfPeople: [],
      contentTypes: [contentTypes[0]],
      selectedContentTypes: [contentTypes[0].key],
      sortBy: SORT_ITEMS.bestMatch.value,
    }),
    [contentTypes],
  );
  const [open, setOpen] = useState(false);
  const [tmpSelectedFilters, setTmpSelectedFilters] = useState(defaultFilters);

  const getAppliedFilters = () => {
    return {
      numberOfPeople: numberOfPeopleFilters[location.pathname] || defaultFilters.numberOfPeople,
      contentTypes: contentTypesFilters[location.pathname] || defaultFilters.contentTypes,
      selectedContentTypes:
        contentTypesFilters[location.pathname]?.map((item) => item.key) || defaultFilters.selectedContentTypes,
      sortBy: sortFilterSelected[location.pathname]?.value || defaultFilters.sortBy,
    };
  };

  const hasFiltersApplied = () => {
    return !equals(getAppliedFilters(), defaultFilters);
  };

  const handleSortBy = (filterValue) => {
    setTmpSelectedFilters((prev) => ({
      ...prev,
      sortBy: filterValue,
    }));
  };

  const removeContentType = (filterItem) => {
    let newContentTypes =
      filterItem.key === "all"
        ? defaultFilters.contentTypes
        : tmpSelectedFilters.contentTypes.filter((item) => item.key !== filterItem.key);

    if (newContentTypes.length === 0) newContentTypes = defaultFilters.contentTypes;

    setTmpSelectedFilters((prev) => ({
      ...prev,
      contentTypes: newContentTypes,
      selectedContentTypes: newContentTypes.map((item) => item.key),
    }));
  };

  const handleContentType = (filterItem) => {
    if (tmpSelectedFilters.selectedContentTypes.includes(filterItem.key) || filterItem.key === "all") {
      removeContentType(filterItem);
    } else {
      setTmpSelectedFilters((prev) => {
        const newContentTypes = [...prev.contentTypes.filter((item) => item.key !== "all"), filterItem];
        const newSelectedContentTypes = [...prev.selectedContentTypes.filter((item) => item !== "all"), filterItem.key];

        if (newContentTypes.length === 0 || newSelectedContentTypes.length === contentTypes.length - 1) {
          return {
            ...prev,
            contentTypes: defaultFilters.contentTypes,
            selectedContentTypes: defaultFilters.selectedContentTypes,
          };
        }

        return {
          ...prev,
          contentTypes: newContentTypes,
          selectedContentTypes: newSelectedContentTypes,
        };
      });
    }
  };

  const removeNumberOfPeople = (filterValue) => {
    const newNumberOfPeople =
      filterValue === "" ? [] : tmpSelectedFilters.numberOfPeople.filter((item) => item !== filterValue);

    setTmpSelectedFilters((prev) => ({
      ...prev,
      numberOfPeople: newNumberOfPeople,
    }));
  };

  const handleNumberOfPeople = (filterValue) => {
    if (tmpSelectedFilters.numberOfPeople.includes(filterValue) || filterValue === "") {
      removeNumberOfPeople(filterValue);
    } else if (tmpSelectedFilters.numberOfPeople.length === numberOfPeople.length - 1) {
      removeNumberOfPeople("");
    } else {
      setTmpSelectedFilters((prev) => ({
        ...prev,
        numberOfPeople: [...prev.numberOfPeople, filterValue],
      }));
    }
  };

  const handleClearAll = () => {
    onClear();
    setTmpSelectedFilters(defaultFilters);
  };

  const handleOnClose = () => {
    setTmpSelectedFilters(getAppliedFilters());
    setOpen(false);
  };

  const handleApply = () => {
    if (!equals(tmpSelectedFilters, getAppliedFilters())) {
      const {
        sortBy: sortBySelected,
        numberOfPeople: numberOfPeopleSelected,
        contentTypes: contentTypesSelected,
      } = tmpSelectedFilters;

      setSortFilterSelected({
        path: location.pathname,
        filterValue: { label: sortBySelected, value: sortBySelected },
      });
      setNumberOfPeopleFilters({ path: location.pathname, filterValue: numberOfPeopleSelected });
      setContentTypesFilters({ path: location.pathname, filterValue: contentTypesSelected });

      setIndexUiState((prev) => ({
        ...prev,
        sortBy: tmpSelectedFilters.sortBy,
        configure: {
          ...prev.configure,
          facetFilters: [
            ...[contentTypesSelected.flatMap((item) => item.value)],
            ...(numberOfPeopleSelected.length > 0
              ? [numberOfPeopleSelected.map((item) => `${ALGOLIA_FILTERS.castNumOptions}:${item}`)]
              : []),
            ...(collectionsFilter ? [collectionsFilter] : []),
          ],
        },
        page: 0,
      }));
    }

    handleOnClose();
  };

  useImperativeHandle(ref, () => ({
    resetFilters: () => {
      setTmpSelectedFilters(defaultFilters);
      setSortFilterSelected({
        path: location.pathname,
        filterValue: { label: defaultFilters.sortBy, value: defaultFilters.sortBy },
      });
      setNumberOfPeopleFilters({ path: location.pathname, filterValue: defaultFilters.numberOfPeople });
      setContentTypesFilters({ path: location.pathname, filterValue: defaultFilters.contentTypes });
    },
  }));

  useEffect(() => {
    if (undefined === contentTypesFilters[location.pathname] || contentTypesFilters[location.pathname].length === 0) {
      setContentTypesFilters({ path: location.pathname, filterValue: defaultFilters.contentTypes });
    }

    setTmpSelectedFilters(getAppliedFilters());
  }, [contentTypesFilters, numberOfPeopleFilters, sortFilterSelected]);

  if (hidden) return null;

  return (
    <>
      <FilterSortButton variant="outlined" startIcon={<TuneIcon />} onClick={() => setOpen(true)}>
        {hasFiltersApplied() && <FilterSortBadge variant="dot" color="primary" data-testid="FilterSortBadge" />}
        Filter & Sort
      </FilterSortButton>
      <Drawer sx={{ zIndex: 1400 }} open={open} onClose={() => handleOnClose()} ModalProps={{ keepMounted: true }}>
        <DrawerListWrapper role="presentation">
          <Header handleClearAll={handleClearAll} onClose={() => handleOnClose()} />
          <NumberOfPeople
            attribute={ALGOLIA_FILTERS.castNumOptions}
            operator="or"
            selectedNumberOfPeople={tmpSelectedFilters.numberOfPeople}
            numberOfPeople={numberOfPeople}
            onFilterClick={handleNumberOfPeople}
          />
          <StyledDivider />
          <ContentTypes
            attribute={ALGOLIA_FILTERS.templateGroupTypeName}
            operator="or"
            selectedContentTypes={tmpSelectedFilters.selectedContentTypes}
            onFilterClick={handleContentType}
            contentTypes={contentTypes}
          />
          <StyledDivider />
          <SortBy items={options} sortBy={tmpSelectedFilters.sortBy} onSortChange={handleSortBy} />
          <ApplyContainer>
            <Button variant="contained" fullWidth color="primary" onClick={handleApply}>
              Apply
            </Button>
          </ApplyContainer>
        </DrawerListWrapper>
      </Drawer>
    </>
  );
});

FilterSort.propTypes = {
  hidden: PropTypes.bool,
  onClear: PropTypes.func,
  contentTypeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      key: PropTypes.string.isRequired,
      value: PropTypes.arrayOf(PropTypes.string).isRequired,
    }),
  ),
  collectionsFilter: PropTypes.arrayOf(PropTypes.string),
};

export default FilterSort;
